import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import {
  ArrowRight,
  ChevronRight,
  Loader2,
  Package,
  Search,
  SlidersHorizontal,
  X,
} from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import {
  listTransferOrganizations,
  listTransfers,
  listWarehouses,
  type TransferCard,
} from "@/api/transfer_check";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { fmtDate, ruNum as num } from "@/lib/format";
import { cn } from "@/lib/utils";

function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

/**
 * Проверка перемещений — поиск/список заказов на перемещение (mobile-first).
 * Поиск по точному значащему номеру; фильтры: статус проверки, организация,
 * откуда/куда (склады); подгрузка по 30 (не старше 3 мес, свежие сверху).
 */
const LS = {
  q: "wh_tc_q",
  org: "wh_tc_org",
  from: "wh_tc_from",
  to: "wh_tc_to",
  check: "wh_tc_check",
};
const lsGet = (k: string) => {
  try {
    return localStorage.getItem(k) ?? "";
  } catch {
    return "";
  }
};

const CHECK_OPTIONS: { value: string; label: string }[] = [
  { value: "", label: "Все" },
  { value: "none", label: "Не проверен" },
  { value: "in_progress", label: "В работе" },
  { value: "sent", label: "В пути на приёмку" },
  { value: "discrepancy", label: "Расхождение" },
  { value: "completed", label: "Проверен" },
];

export function TransferCheckListPage(): React.ReactElement {
  const navigate = useNavigate();
  // Фильтры из адреса ПЕРЕКРЫВАЮТ запомненные: сюда приходят с экрана ФБС по
  // кнопке «Перемещения на склад ФБС», и она знает, что именно нужно показать
  // (организация-владелец, склад-источник, склад ФБС). Прошлый выбор в этот
  // момент — не память, а помеха.
  const [params] = useSearchParams();
  const seed = (key: string, ls: string) => params.get(key) ?? lsGet(ls);
  const [rawQuery, setRawQuery] = useState(() => (params.has("org") ? "" : lsGet(LS.q)));
  const query = useDebounced(rawQuery.trim(), 350);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [orgRef, setOrgRef] = useState<string>(() => seed("org", LS.org));
  const [fromRef, setFromRef] = useState<string>(() => seed("from", LS.from));
  const [toRef, setToRef] = useState<string>(() => seed("to", LS.to));
  const [checkStatus, setCheckStatus] = useState<string>(() => lsGet(LS.check));

  useEffect(() => {
    try {
      localStorage.setItem(LS.q, rawQuery);
      localStorage.setItem(LS.org, orgRef);
      localStorage.setItem(LS.from, fromRef);
      localStorage.setItem(LS.to, toRef);
      localStorage.setItem(LS.check, checkStatus);
    } catch {
      /* приватный режим — не критично */
    }
  }, [rawQuery, orgRef, fromRef, toRef, checkStatus]);

  const orgsQ = useQuery({
    queryKey: ["transfer-check-orgs"],
    queryFn: () => listTransferOrganizations(),
    enabled: filtersOpen,
    staleTime: 5 * 60_000,
  });
  const whQ = useQuery({
    queryKey: ["transfer-check-warehouses"],
    queryFn: () => listWarehouses(),
    enabled: filtersOpen,
    staleTime: 5 * 60_000,
  });

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isError,
    error,
  } = useInfiniteQuery({
    queryKey: [
      "transfer-check-list",
      query,
      orgRef,
      fromRef,
      toRef,
      checkStatus,
    ],
    queryFn: ({ pageParam }) =>
      listTransfers({
        q: query,
        organization_ref: orgRef,
        from_ref: fromRef,
        to_ref: toRef,
        check_status: checkStatus,
        skip: pageParam,
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) =>
      lastPage.has_more
        ? allPages.reduce((n, p) => n + p.items.length, 0)
        : undefined,
  });

  const rows = data?.pages.flatMap((p) => p.items) ?? [];
  const activeFilters =
    (orgRef ? 1 : 0) +
    (fromRef ? 1 : 0) +
    (toRef ? 1 : 0) +
    (checkStatus ? 1 : 0);

  const sentinelRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const io = new IntersectionObserver((entries) => {
      if (entries[0]?.isIntersecting && hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    });
    io.observe(el);
    return () => io.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  return (
    <div className="mx-auto max-w-xl space-y-3">
      <h1 className="text-xl font-semibold">Проверка перемещений</h1>

      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-2 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={rawQuery}
            onChange={(e) => setRawQuery(e.target.value)}
            inputMode="numeric"
            placeholder="Номер перемещения"
            className="pl-8"
          />
          {rawQuery && (
            <button
              type="button"
              onClick={() => setRawQuery("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground"
              aria-label="Очистить"
            >
              <X className="size-4" />
            </button>
          )}
        </div>
        <Button
          variant={activeFilters ? "default" : "outline"}
          size="icon"
          onClick={() => setFiltersOpen((s) => !s)}
          aria-label="Фильтры"
          className="relative shrink-0"
        >
          <SlidersHorizontal className="size-4" />
          {activeFilters > 0 && (
            <span className="absolute -right-1 -top-1 flex size-4 items-center justify-center rounded-full bg-primary text-[10px] text-primary-foreground">
              {activeFilters}
            </span>
          )}
        </Button>
      </div>

      {filtersOpen && (
        <Card className="space-y-3 p-3">
          <div>
            <div className="mb-1 text-xs font-medium text-muted-foreground">
              Статус проверки
            </div>
            <div className="flex flex-wrap gap-1.5">
              {CHECK_OPTIONS.map((o) => (
                <Chip
                  key={o.value || "all"}
                  active={checkStatus === o.value}
                  onClick={() => setCheckStatus(o.value)}
                >
                  {o.label}
                </Chip>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <WarehouseSelect
              label="Откуда"
              value={fromRef}
              onChange={setFromRef}
              options={whQ.data}
              loading={whQ.isLoading}
            />
            <WarehouseSelect
              label="Куда"
              value={toRef}
              onChange={setToRef}
              options={whQ.data}
              loading={whQ.isLoading}
            />
          </div>
          <div>
            <div className="mb-1 text-xs font-medium text-muted-foreground">
              Организация
            </div>
            <div className="flex flex-wrap gap-1.5">
              {orgsQ.isLoading && (
                <Loader2 className="size-4 animate-spin text-muted-foreground" />
              )}
              {orgsQ.data?.map((o) => (
                <Chip
                  key={o.ref}
                  active={orgRef === o.ref}
                  onClick={() => setOrgRef(orgRef === o.ref ? "" : o.ref)}
                >
                  {o.name}
                </Chip>
              ))}
            </div>
          </div>
          {activeFilters > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setOrgRef("");
                setFromRef("");
                setToRef("");
                setCheckStatus("");
              }}
            >
              Сбросить фильтры
            </Button>
          )}
        </Card>
      )}

      {isLoading && (
        <div className="flex justify-center py-10 text-muted-foreground">
          <Loader2 className="size-6 animate-spin" />
        </div>
      )}
      {isError && (
        <Card className="border-destructive/50 p-3 text-sm text-destructive">
          Не удалось загрузить перемещения:{" "}
          {(error as Error)?.message ?? "ошибка"}
        </Card>
      )}
      {!isLoading && !isError && rows.length === 0 && (
        <p className="py-10 text-center text-sm text-muted-foreground">
          {query
            ? `Перемещение № ${query} не найдено`
            : "Нет перемещений за последние 3 месяца"}
        </p>
      )}

      <div className="space-y-2">
        {rows.map((r) => (
          <TransferCardView
            key={r.ref}
            row={r}
            onClick={() => navigate(`/transfer-check/transfers/${r.ref}`)}
          />
        ))}
      </div>

      <div ref={sentinelRef} className="h-6" />
      {isFetchingNextPage && (
        <div className="flex justify-center py-2 text-muted-foreground">
          <Loader2 className="size-5 animate-spin" />
        </div>
      )}
    </div>
  );
}

function WarehouseSelect({
  label,
  value,
  onChange,
  options,
  loading,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options?: { ref: string; name: string }[];
  loading: boolean;
}): React.ReactElement {
  return (
    <label className="space-y-1">
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm"
      >
        <option value="">{loading ? "Загрузка…" : "Любой"}</option>
        {options?.map((o) => (
          <option key={o.ref} value={o.ref}>
            {o.name}
          </option>
        ))}
      </select>
    </label>
  );
}

function Chip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "rounded-full border px-3 py-1 text-xs transition-colors",
        active
          ? "border-primary bg-primary text-primary-foreground"
          : "border-input bg-background text-foreground hover:bg-accent",
      )}
    >
      {children}
    </button>
  );
}

function CheckBadge({
  status,
}: {
  status: TransferCard["check_status"];
}): React.ReactElement | null {
  if (status === "completed") {
    return (
      <span className="rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] font-medium text-emerald-700">
        ✓ Проверен
      </span>
    );
  }
  // Через приёмку: отправлено и ждёт получателя / расхождение на разборе.
  if (status === "sent") {
    return (
      <span className="rounded bg-sky-100 px-1.5 py-0.5 text-[10px] font-medium text-sky-700">
        В пути на приёмку
      </span>
    );
  }
  if (status === "discrepancy") {
    return (
      <span className="rounded bg-amber-200 px-1.5 py-0.5 text-[10px] font-semibold text-amber-900">
        Расхождение — на разборе
      </span>
    );
  }
  if (status === "in_progress") {
    return (
      <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-700">
        В работе
      </span>
    );
  }
  return null;
}

function TransferCardView({
  row,
  onClick,
}: {
  row: TransferCard;
  onClick: () => void;
}): React.ReactElement {
  return (
    <button type="button" onClick={onClick} className="w-full text-left">
      <Card className="flex items-center gap-3 p-3 transition-colors hover:bg-accent active:bg-accent">
        <div className="min-w-0 flex-1 space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-semibold">{row.number}</span>
            <span className="text-xs text-muted-foreground">
              {fmtDate(row.date)}
            </span>
            {row.state_name && (
              <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">
                {row.state_name}
              </span>
            )}
            <CheckBadge status={row.check_status} />
          </div>
          <div className="truncate text-sm">{row.organization_name}</div>
          <div className="flex items-center gap-1 text-sm text-muted-foreground">
            <span className="truncate">{row.from_name || "—"}</span>
            <ArrowRight className="size-3 shrink-0" />
            <span className="truncate">{row.to_name || "—"}</span>
          </div>
          <div className="flex items-center gap-1 text-xs text-muted-foreground">
            <Package className="size-3" />
            {num.format(row.qty)} шт
          </div>
          {/* Комментарий: у завоза в комнату ФБС там названо юрлицо, ради
              которого везут, — реквизита «юрлицо» у документа нет. */}
          {row.comment && (
            <div className="truncate text-xs text-muted-foreground">
              {row.comment}
            </div>
          )}
        </div>
        <ChevronRight className="size-5 shrink-0 text-muted-foreground" />
      </Card>
    </button>
  );
}
